import type { Store } from "../data/store.js";
import { categorySuggestions } from "../lib/categories.js";
import { el } from "../lib/dom.js";
import { CATEGORY_SUGGESTIONS } from "./itemForm.js";

/**
 * The page's one `<datalist>` of categories (g79), which every Category field
 * names. Fed from the store the page was given and nothing else: signed in,
 * that is the account's own lists; on a shared list's page, that list alone,
 * so a visitor never sees another scope's values.
 *
 * Repainted at most once a task, because a full download announces every row
 * one by one, and only when the values actually changed, so an open
 * suggestion list is not rebuilt under a finger for a tick elsewhere.
 */
export function suggestCategories(store: Pick<Store, "lists" | "allItems" | "onLists" | "onAllItems">): {
  node: HTMLDataListElement;
  dispose(): void;
} {
  const node = el("datalist", { attrs: { id: CATEGORY_SUGGESTIONS } });
  let painted: string | undefined;
  let queued = false;
  const paint = (): void => {
    queued = false;
    const values = categorySuggestions(store.lists(), store.allItems());
    const key = JSON.stringify(values);
    if (key === painted) return;
    painted = key;
    node.replaceChildren(...values.map((value) => el("option", { attrs: { value } })));
  };
  const schedule = (): void => {
    if (queued) return;
    queued = true;
    queueMicrotask(paint);
  };
  paint();
  // A kind changing to or from Shopping changes what counts, as an item does.
  const stops = [store.onLists(schedule), store.onAllItems(schedule)];
  return { node, dispose: () => stops.forEach((stop) => stop()) };
}
